import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * Tiny global "is the user-guide drawer open?" state. Exposed so any
 * component anywhere in the tree (nav link, floating button, keyboard
 * shortcut, future contextual ?-icons) can open the same drawer
 * without prop-drilling.
 */
interface HelpContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);
  return (
    <HelpContext.Provider value={{ isOpen, open, close, toggle }}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextValue => {
  const ctx = useContext(HelpContext);
  if (!ctx) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return ctx;
};
